/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/manager/schedule/ScheduleAuditPanel.tsx
 * PURPOSE: Panel [6] "Nhật ký thay đổi lịch" (BR-SCHD-012, 🆕 CHƯA CÓ BR
 *          chính thức — wireframe là nguồn đầu tiên) — SC-009-SCHEDULE-GRID.
 *          Dock bên phải (≥1024px, ĐẨY lưới sang trái) / modal Tablet-Mobile
 *          (<1024px). Thanh điều hướng tuần RIÊNG panel (ĐỘC LẬP lưới chính),
 *          3 bộ lọc client-side AND (Người/Đường/Loại), phân trang cursor
 *          ("Xem thêm (cũ hơn)"), chỉ đọc tuyệt đối (không drill-down, không
 *          hành động ghi nào trên thẻ).
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-14
 * COMPLIANCE: SDLC 6.5.0 + NQH Brand Design System + Zero Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Manager — tra cứu lịch sử thao tác chỉnh sửa lịch (giải quyết
 *   tranh chấp kiểu "ai xoá ca của tôi")
 * - Impact: Operations — minh bạch thao tác, KHÔNG phải quyền ghi (mọi vai
 *   xem được lưới, kể cả vai chỉ đọc, đều xem được panel này)
 * - Stakeholders: Branch Managers, Shift Managers, Head Chef
 *
 * @technical-architecture
 * - Layer: Presentation Layer (dock/modal + client-side filter trên dữ liệu
 *   đã tải qua cursor pagination)
 * - Pattern: useInfiniteQuery (useScheduleAuditHistory) — filter Người/Đường/
 *   Loại áp dụng TRÊN client, KHÔNG gọi lại API mỗi lần đổi bộ lọc
 * - Dependencies: useManagerSchedule (useScheduleAuditHistory), managerApi
 *   (ScheduleAudit* types), PortalMenu, scheduleShared
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md
 *   §SC-009-SCHEDULE-GRID ANNOTATION "[6] Nhật ký thay đổi lịch" (dòng ~889-936)
 *   + mockup `[6]`/`[6b]`/`[6c]`/`[6c2]`/`[6d]`/`[6e]` (dòng ~3067-3170) +
 *   mobile `[MM-4]` (dòng ~3941) + STATE "Panel Nhật ký" (dòng ~3751-3766).
 * - Prototype: docs/02-design/04-ui-ux-design/design-imports/ui-ux-nqhbot/v0/
 *   schedule/manager/sc009-build-prototype.html `.audit-panel*`/`renderAuditPanel()`
 *   (dòng ~83-103, ~2293-2367) — ground truth CSS + hành vi; ĐÃ LỆCH khỏi
 *   wireframe đích ở 2 điểm (xem ghi chú ROUTE_GROUPS bên dưới).
 * - Backend: backend/app/api/v2/endpoints/manager_schedule.py
 *   `GET /manager/schedule/audit-history` (AuditHistoryResponse/AuditHistoryItem/
 *   AuditAffectedInfo, dòng ~755-843, ~2466-2721).
 * - Hook: frontend/src/hooks/useManagerSchedule.ts `useScheduleAuditHistory`
 * - API: frontend/src/services/api/managerApi.ts `getScheduleAuditHistory` +
 *   `ScheduleAudit*` types
 * - Page: frontend/src/pages/manager/ManagerSchedulePage.tsx (nút toolbar
 *   `[Nhật ký]` + layout `.scheduleLayout`)
 * ===============================================================================
 */

import React, { useEffect, useRef, useState } from "react";
import { useScheduleAuditHistory } from "../../../hooks/useManagerSchedule";
import type {
  ScheduleAuditActorInfo,
  ScheduleAuditContentKind,
  ScheduleAuditHistoryItem,
} from "../../../services/api/managerApi";
import { PortalMenu, type PortalMenuAnchor } from "../../../components/common/PortalMenu";
import { addDaysISO, formatDayMonth } from "./scheduleShared";
import styles from "../ManagerSchedulePage.module.css";

const PAGE_SIZE = 25;

// ── Icons (bản local, cùng SVG path convention với ManagerSchedulePage.tsx —
// không import cross-file để giữ mỗi modal/panel độc lập, khớp QuickEntryModal). ──

const CloseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={22} height={22} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const ChevronDownIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={16} height={16} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
  </svg>
);
const ChevronLeftIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={18} height={18} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
  </svg>
);
const ChevronRightIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={18} height={18} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);
const HistoryIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={18} height={18} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2M4.5 12a7.5 7.5 0 1 1 2.4 5.5M4.5 12H8m-3.5 0V8.5" />
  </svg>
);

// ── Nhãn VI — tiêu đề thẻ = nhãn ROUTE (11 giá trị wireframe [6c]/[6e] + 2
// hành động "Công bố"/"Chỉ còn trong lịch sử" ngoài domain gán/gỡ nhưng vẫn
// hiện trong CÙNG panel/menu). KHÔNG dùng `action_label_vi` của backend làm
// tiêu đề thẻ — field đó phục vụ mục đích khác (vd CELL_UPDATE op=assign trả
// "Gán ca", op=update_shift trả "Sửa ca tại chỗ", nhưng CẢ HAI cùng route=
// "cell" → wireframe muốn CẢ HAI cùng hiện tiêu đề "Chọn trên ô", vì route mới
// là "bằng đường nào" mà [6e] yêu cầu đọc được — action_label_vi mịn hơn theo
// TRỤC KHÁC (assign/remove/update_shift), không khớp trục "7 đường gán + 4
// đường gỡ" panel này cần). Xem docstring `_schedule_action_label_vi` trong
// manager_schedule.py để đối chiếu 2 trục này KHÔNG trùng nhau.
const ROUTE_LABEL: Record<string, string> = {
  cell: "Chọn trên ô",
  rect: "Kéo chọn vùng",
  range: "Chọn khoảng ngày",
  quick_entry: "Nhập nhanh",
  import: "Nhập tệp",
  copy: "Sao chép lịch",
  proposal_approve: "Duyệt đề xuất",
  remove_single: "Bỏ ca",
  remove_day: "Bỏ hết ca trong ngày",
  bulk_off: "Nghỉ hàng loạt",
  proposal_reject: "Từ chối đề xuất",
  publish: "Xuất bản tuần",
  save_draft: "Lưu nháp lịch tuần",
};

/**
 * [6c] MENU "Đường ▼" — 4 nhóm. LỆCH prototype `ROUTE_GROUPS` (prototype có
 * 5 nhóm, thêm "Sửa tại chỗ" cho route nội bộ `edit`) — wireframe ĐÍCH (mockup
 * `[6c]` dòng ~3111-3132, xác nhận lại 2026-09-14) chỉ có 4 nhóm, và backend
 * `_schedule_audit_route()` KHÔNG có giá trị route nào tên "edit" (op
 * update_shift dùng CHUNG route="cell" với op assign — xem comment ROUTE_LABEL
 * trên). Theo wireframe đích (mới hơn, đã xác nhận qua response model thật),
 * KHÔNG theo prototype ở điểm này.
 */
const ROUTE_GROUPS: Array<[string, string[]]> = [
  ["Gán ca", ["cell", "rect", "range", "quick_entry", "import", "copy", "proposal_approve"]],
  ["Gỡ / từ chối", ["remove_single", "remove_day", "bulk_off", "proposal_reject"]],
  ["Công bố", ["publish"]],
  ["Chỉ còn trong lịch sử", ["save_draft"]],
];

const KIND_LABEL: Record<ScheduleAuditContentKind, string> = {
  shift_code: "Ca theo mã ca",
  custom_hours: "Khối giờ tuỳ chỉnh",
  custom_hours_ot: "Khối giờ tuỳ chỉnh · OT",
};

/** route (khi có) LÀM TIÊU ĐỀ; publish/save_draft (route=null) suy từ `affected.op`
 * — 2 action DUY NHẤT trong 13 action SCHEDULE_AUDIT_ACTIONS không có route
 * (xem `_schedule_audit_route()` — mọi nhánh khác luôn trả 1 trong 11 giá trị). */
function auditRouteKey(item: ScheduleAuditHistoryItem): string {
  if (item.affected.route) return item.affected.route;
  if (item.affected.op === "publish") return "publish";
  if (item.affected.op === "save_draft") return "save_draft";
  return item.affected.op; // fallback an toàn — không rơi vào domain 13 action đã biết
}

function auditTitle(item: ScheduleAuditHistoryItem): string {
  return ROUTE_LABEL[auditRouteKey(item)] ?? item.action_label_vi;
}

/**
 * "cho ai · ca gì ngày nào" — CHỈ hiện phần THẬT SỰ có trong response, KHÔNG
 * bịa. GAP THẬT (xem báo cáo cuối): nhiều route batch (rect/quick_entry/
 * import/copy/bulk_off/proposal_week_review) backend chỉ ghi `date_start`/
 * `date_end`/tuần — KHÔNG có 1 ngày đơn để lộ qua field `affected.date` (chỉ
 * `route=cell` và 2 route proposal_approve/proposal_reject đơn-ca có `date`).
 * Tương tự `shift_code` CHỈ chắc chắn có ở `route=cell` (assign) và
 * `route=rect` không-OT (BULK_ASSIGN, template.code) — mọi route khác None.
 */
function auditTargetText(item: ScheduleAuditHistoryItem): string {
  const a = item.affected;
  const parts: string[] = [];
  if (a.staff.length === 1) parts.push(a.staff[0].full_name);
  else if (a.staff.length > 1) parts.push(`${a.staff.length} nhân viên`);
  if (a.date) parts.push(formatDayMonth(a.date));
  if (a.shift_code) parts.push(a.shift_code);
  return parts.length > 0 ? parts.join(" · ") : "—";
}

/** Dòng phụ: content_kind=custom_hours_ot → "· Đăng ký ngoài giờ"; route=copy
 * → "(nguồn: {dd/MM})"; route=proposal_* + is_self_proposed → "(nhân viên tự
 * đề xuất)". Theo backend contract, tối đa 1 trong 3 điều kiện khớp/dòng
 * (custom_hours_ot chỉ ở route=rect; source_date chỉ ở route=copy;
 * is_self_proposed chỉ ở route=proposal_*) — vẫn viết dạng mảng để không giả
 * định thứ tự loại trừ lẫn nhau mãi đúng nếu backend mở rộng sau này. */
function auditExtraLines(item: ScheduleAuditHistoryItem): string[] {
  const a = item.affected;
  const lines: string[] = [];
  if (a.content_kind === "custom_hours_ot") lines.push("· Đăng ký ngoài giờ");
  if (a.source_date) lines.push(`(nguồn: ${formatDayMonth(a.source_date)})`);
  if (a.is_self_proposed) lines.push("(nhân viên tự đề xuất)");
  return lines;
}

function formatDateTimeVi(iso: string): string {
  const d = new Date(iso);
  const p2 = (n: number) => String(n).padStart(2, "0");
  return `${p2(d.getDate())}/${p2(d.getMonth() + 1)} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
}

/** Số tuần ISO — bản copy cục bộ của `isoWeekNumber` (ManagerSchedulePage.tsx
 * dòng ~320), giữ panel này KHÔNG phụ thuộc file cha (component độc lập,
 * cùng convention với QuickEntryModal/CopyScheduleModal không import ngược
 * từ trang cha). Toán học giống hệt, không đổi hành vi. */
function isoWeekNumber(iso: string): number {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

type ViewportMode = "desktop" | "tablet" | "mobile";

function detectViewport(): ViewportMode {
  if (typeof window === "undefined") return "desktop";
  if (window.innerWidth < 768) return "mobile";
  if (window.innerWidth < 1024) return "tablet";
  return "desktop";
}

type FilterKey = "who" | "route" | "kind";

export interface ScheduleAuditPanelProps {
  /** Tuần đang xem trên LƯỚI CHÍNH lúc bật panel — CHỈ dùng làm giá trị mặc
   * định ban đầu; sau đó panel tự quản lý tuần riêng, ĐỘC LẬP hoàn toàn với
   * lưới chính (ANNOTATION [6]: "không đồng bộ theo [CAL] toolbar"). */
  initialWeekStart: string;
  /** 1=Thứ 2…7=CN (ScheduleRulesInfo.week_start_day) — dùng tính "tuần hiện
   * tại" để khoá nút `›`/xác định "Tuần này". Mặc định 1 (Thứ 2). */
  weekStartDay?: number;
  /** "Chi nhánh {tên chi nhánh}" dưới tiêu đề panel — null/undefined thì ẩn dòng đó. */
  branchName?: string | null;
  onClose: () => void;
}

const ScheduleAuditPanel: React.FC<ScheduleAuditPanelProps> = ({
  initialWeekStart,
  weekStartDay = 1,
  branchName,
  onClose,
}) => {
  const [weekStart, setWeekStart] = useState(initialWeekStart);
  const [who, setWho] = useState<string>("ALL");
  const [routeFilter, setRouteFilter] = useState<string>("ALL");
  const [kindFilter, setKindFilter] = useState<string>("ALL");
  const [openFilter, setOpenFilter] = useState<FilterKey | null>(null);
  const [filterAnchor, setFilterAnchor] = useState<PortalMenuAnchor | null>(null);
  const [nextPageError, setNextPageError] = useState(false);

  const [viewport, setViewport] = useState<ViewportMode>(detectViewport);
  useEffect(() => {
    const mqMobile = window.matchMedia("(max-width: 767px)");
    const mqTablet = window.matchMedia("(max-width: 1023px)");
    const update = () => setViewport(detectViewport());
    update();
    mqMobile.addEventListener("change", update);
    mqTablet.addEventListener("change", update);
    return () => {
      mqMobile.removeEventListener("change", update);
      mqTablet.removeEventListener("change", update);
    };
  }, []);

  // Mất mạng (12-state "NHẬT-KÝ-MẤT-MẠNG") — cùng pattern navigator.onLine +
  // listener online/offline đã dùng ở ManagerAttendancePage.tsx (KHÔNG dùng
  // hooks/useOnlineStatus.ts — hook đó polling /api/v1/health mỗi 30s, nặng
  // hơn mức panel con này cần).
  const [isOnline, setIsOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useScheduleAuditHistory({ weekStart, limit: PAGE_SIZE });

  // Có mạng lại → tự tải lại (khớp STATE "Có mạng lại → NHẬT-KÝ-ĐANG-TẢI").
  const prevOnlineRef = useRef(isOnline);
  useEffect(() => {
    if (isOnline && !prevOnlineRef.current) refetch();
    prevOnlineRef.current = isOnline;
  }, [isOnline, refetch]);

  // Đổi tuần panel → bộ lọc "Người ▼" dễ trỏ vào actor không còn trong tuần
  // mới (menu tự sinh từ dữ liệu đã tải của TUẦN ĐANG XEM) — về ALL cho sạch;
  // cùng lúc dọn cờ lỗi "Xem thêm" của tuần cũ.
  useEffect(() => {
    setWho("ALL");
    setRouteFilter("ALL");
    setKindFilter("ALL");
    setNextPageError(false);
    setOpenFilter(null);
  }, [weekStart]);

  const allItems: ScheduleAuditHistoryItem[] = data?.pages.flatMap((p) => p.items) ?? [];
  const hasAnyFilter = who !== "ALL" || routeFilter !== "ALL" || kindFilter !== "ALL";
  const filtered = allItems.filter((item) => {
    if (who !== "ALL" && item.actor.id !== who) return false;
    if (routeFilter !== "ALL" && auditRouteKey(item) !== routeFilter) return false;
    if (kindFilter !== "ALL" && item.affected.content_kind !== kindFilter) return false;
    return true;
  });

  const actorOptions: ScheduleAuditActorInfo[] = Array.from(
    new Map(allItems.map((it) => [it.actor.id, it.actor])).values(),
  );

  const currentWeekStart = (() => {
    const today = new Date();
    const pyWeekday = (today.getDay() + 6) % 7;
    const startWeekday = ((weekStartDay - 1) % 7 + 7) % 7;
    const daysSinceStart = ((pyWeekday - startWeekday) % 7 + 7) % 7;
    const monday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - daysSinceStart);
    const p2 = (n: number) => String(n).padStart(2, "0");
    return `${monday.getFullYear()}-${p2(monday.getMonth() + 1)}-${p2(monday.getDate())}`;
  })();
  const isAtOrAfterCurrentWeek = weekStart >= currentWeekStart;

  const openFilterMenu = (key: FilterKey) => (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (openFilter === key) {
      setOpenFilter(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setFilterAnchor({ top: rect.top, bottom: rect.bottom, right: window.innerWidth - rect.right });
    setOpenFilter(key);
  };

  const handleLoadMore = async () => {
    setNextPageError(false);
    const result = await fetchNextPage();
    if (result.isError) setNextPageError(true);
  };

  const weekLabel = `Tuần ${isoWeekNumber(weekStart)} · ${formatDayMonth(weekStart)}–${formatDayMonth(addDaysISO(weekStart, 6))}`;

  // ── Body theo 12-state (STATE "Panel Nhật ký", WIREFRAME dòng ~3751-3766) ──
  const renderBody = () => {
    if (isLoading) {
      return (
        <>
          <div className={styles.auditSkeleton} aria-hidden="true" />
          <div className={styles.auditSkeleton} aria-hidden="true" />
          <div className={styles.auditSkeleton} aria-hidden="true" />
        </>
      );
    }
    if (isError) {
      if (!isOnline) {
        return (
          <div className={`${styles.banner} ${styles.bannerLock}`} role="status">
            Mất kết nối
          </div>
        );
      }
      return (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert" style={{ flexDirection: "column", alignItems: "flex-start", gap: "var(--nqh-space-2)" }}>
          <span>Không tải được nhật ký</span>
          <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => refetch()}>
            Thử lại
          </button>
        </div>
      );
    }
    if (allItems.length === 0) {
      // NHẬT-KÝ-RỖNG — cố ý KHÔNG bộ lọc/số đếm/nút "Xem thêm" (ANNOTATION [6]).
      return <p className={styles.auditPanelFootNote}>Chưa có thao tác nào trong tuần này.</p>;
    }
    return (
      <>
        <p className={styles.auditPanelFootNote} style={{ textAlign: "left", marginTop: 0 }}>
          Bộ lọc áp trên dữ liệu đã tải.
        </p>
        <div className={styles.auditPanelFilters}>
          <span className={styles.menuAnchor}>
            <button
              type="button"
              className={`${styles.selectBtn} ${who !== "ALL" ? styles.selectBtnActive : ""}`}
              aria-haspopup="true"
              aria-expanded={openFilter === "who"}
              onClick={openFilterMenu("who")}
            >
              <span>Người{who !== "ALL" ? ": 1" : ""}</span>
              <ChevronDownIcon />
            </button>
            {openFilter === "who" && filterAnchor && (
              <PortalMenu anchor={filterAnchor} onClose={() => setOpenFilter(null)} ariaLabel="Người" minWidth={220}>
                <button
                  type="button"
                  role="menuitem"
                  className={`${styles.menuItem} ${who === "ALL" ? styles.menuItemActive : ""}`}
                  onClick={() => { setWho("ALL"); setOpenFilter(null); }}
                >
                  Tất cả
                </button>
                {actorOptions.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    role="menuitem"
                    className={`${styles.menuItem} ${who === a.id ? styles.menuItemActive : ""}`}
                    onClick={() => { setWho(a.id); setOpenFilter(null); }}
                  >
                    {a.full_name}
                  </button>
                ))}
                <div className={styles.menuFootNote}>
                  Chỉ gồm người xuất hiện trong {allItems.length} thao tác đã tải.
                </div>
              </PortalMenu>
            )}
          </span>

          <span className={styles.menuAnchor}>
            <button
              type="button"
              className={`${styles.selectBtn} ${routeFilter !== "ALL" ? styles.selectBtnActive : ""}`}
              aria-haspopup="true"
              aria-expanded={openFilter === "route"}
              onClick={openFilterMenu("route")}
            >
              <span>Đường{routeFilter !== "ALL" ? ": 1" : ""}</span>
              <ChevronDownIcon />
            </button>
            {openFilter === "route" && filterAnchor && (
              <PortalMenu anchor={filterAnchor} onClose={() => setOpenFilter(null)} ariaLabel="Đường" minWidth={220}>
                <button
                  type="button"
                  role="menuitem"
                  className={`${styles.menuItem} ${routeFilter === "ALL" ? styles.menuItemActive : ""}`}
                  onClick={() => { setRouteFilter("ALL"); setOpenFilter(null); }}
                >
                  Tất cả
                </button>
                {ROUTE_GROUPS.map(([groupLabel, keys]) => (
                  <React.Fragment key={groupLabel}>
                    <div className={styles.menuGroupLabel}>{groupLabel}</div>
                    {keys.map((k) => (
                      <button
                        key={k}
                        type="button"
                        role="menuitem"
                        className={`${styles.menuItem} ${routeFilter === k ? styles.menuItemActive : ""}`}
                        onClick={() => { setRouteFilter(k); setOpenFilter(null); }}
                      >
                        {ROUTE_LABEL[k]}
                      </button>
                    ))}
                  </React.Fragment>
                ))}
              </PortalMenu>
            )}
          </span>

          <span className={styles.menuAnchor}>
            <button
              type="button"
              className={`${styles.selectBtn} ${kindFilter !== "ALL" ? styles.selectBtnActive : ""}`}
              aria-haspopup="true"
              aria-expanded={openFilter === "kind"}
              onClick={openFilterMenu("kind")}
            >
              <span>Loại{kindFilter !== "ALL" ? ": 1" : ""}</span>
              <ChevronDownIcon />
            </button>
            {openFilter === "kind" && filterAnchor && (
              <PortalMenu anchor={filterAnchor} onClose={() => setOpenFilter(null)} ariaLabel="Loại" minWidth={200}>
                <button
                  type="button"
                  role="menuitem"
                  className={`${styles.menuItem} ${kindFilter === "ALL" ? styles.menuItemActive : ""}`}
                  onClick={() => { setKindFilter("ALL"); setOpenFilter(null); }}
                >
                  Tất cả
                </button>
                {(Object.keys(KIND_LABEL) as ScheduleAuditContentKind[]).map((k) => (
                  <button
                    key={k}
                    type="button"
                    role="menuitem"
                    className={`${styles.menuItem} ${kindFilter === k ? styles.menuItemActive : ""}`}
                    onClick={() => { setKindFilter(k); setOpenFilter(null); }}
                  >
                    {KIND_LABEL[k]}
                  </button>
                ))}
              </PortalMenu>
            )}
          </span>
        </div>

        <div className={styles.auditPanelCount}>
          {hasAnyFilter ? `${filtered.length}/${allItems.length} thao tác (đã lọc)` : `${allItems.length} thao tác`}
        </div>

        {filtered.length === 0 ? (
          // NHẬT-KÝ-LỌC-RỖNG — giữ bộ lọc để người dùng tự nới lại.
          <p className={styles.auditPanelFootNote} style={{ textAlign: "left" }}>
            Không có thao tác nào khớp bộ lọc.
          </p>
        ) : (
          filtered.map((item) => {
            const extra = auditExtraLines(item);
            return (
              <div className={styles.auditEntry} key={item.id}>
                <div className={styles.auditEntryHead}>
                  <span className={styles.auditEntryTime}>{formatDateTimeVi(item.created_at)}</span>{" "}
                  <span className={styles.auditEntryAction}>{auditTitle(item)}</span>
                </div>
                <div className={styles.auditEntryMeta}>{item.actor.full_name}</div>
                <div className={styles.auditEntryTarget}>{auditTargetText(item)}</div>
                {extra.map((line) => (
                  <div className={styles.auditEntryExtra} key={line}>
                    {line}
                  </div>
                ))}
                {!item.success && <div className={styles.auditEntryFailed}>Thao tác không thành công</div>}
              </div>
            );
          })
        )}

        {filtered.length > 0 &&
          (nextPageError ? (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSecondary}`}
              style={{ width: "100%", marginTop: "var(--nqh-space-2)" }}
              onClick={handleLoadMore}
            >
              Không tải thêm được · Thử lại
            </button>
          ) : hasNextPage ? (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSecondary}`}
              style={{ width: "100%", marginTop: "var(--nqh-space-2)" }}
              disabled={isFetchingNextPage}
              onClick={handleLoadMore}
            >
              {isFetchingNextPage && <span className={styles.spinner} aria-hidden="true" />}
              Xem thêm (cũ hơn)
            </button>
          ) : (
            <p className={styles.auditPanelFootNote}>Đã tải hết lịch sử tuần này.</p>
          ))}
        <p className={styles.auditPanelFootNote}>Nhật ký bất biến — không sửa/xoá được.</p>
      </>
    );
  };

  const headContent = (
    <>
      <div className={viewport === "desktop" ? styles.auditPanelHead : styles.modalHead}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--nqh-space-2)", flex: 1 }}>
          <HistoryIcon />
          <span className={viewport === "desktop" ? undefined : styles.modalTitle} id="scheduleAuditTitle">
            Nhật ký thay đổi lịch
          </span>
        </span>
        <button type="button" className={styles.btnIcon} aria-label="Đóng" onClick={onClose}>
          <CloseIcon />
        </button>
      </div>
      {branchName && (
        <div className={styles.auditPanelBranch} style={{ padding: viewport === "desktop" ? "0 var(--nqh-space-4) var(--nqh-space-2)" : "0 var(--nqh-space-6) var(--nqh-space-2)" }}>
          Chi nhánh {branchName}
        </div>
      )}
      <div className={styles.auditPanelWeeknav}>
        <button
          type="button"
          className={styles.btnIcon}
          aria-label="Tuần trước"
          onClick={() => setWeekStart((w) => addDaysISO(w, -7))}
        >
          <ChevronLeftIcon />
        </button>
        <span className={styles.weekNavLabel}>{weekLabel}</span>
        <button
          type="button"
          className={styles.btnIcon}
          aria-label="Tuần sau"
          disabled={isAtOrAfterCurrentWeek}
          onClick={() => setWeekStart((w) => addDaysISO(w, 7))}
        >
          <ChevronRightIcon />
        </button>
        <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setWeekStart(currentWeekStart)}>
          Tuần này
        </button>
      </div>
    </>
  );

  if (viewport === "desktop") {
    return (
      <aside className={styles.auditPanel} aria-label="Nhật ký thay đổi lịch">
        {headContent}
        <div className={styles.auditPanelBody}>{renderBody()}</div>
      </aside>
    );
  }

  return (
    <div className={styles.modalOverlay} onMouseDown={onClose}>
      <div
        className={`${styles.modal} ${styles.modalAudit}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="scheduleAuditTitle"
        onMouseDown={(e) => e.stopPropagation()}
        style={{ display: "flex", flexDirection: "column" }}
      >
        {headContent}
        <div className={styles.modalBody} style={{ flex: "1 1 auto" }}>
          {renderBody()}
        </div>
      </div>
    </div>
  );
};

export default ScheduleAuditPanel;
